import { supabase } from '../lib/supabase';

// Get all active featured properties (public access)
export const getActiveFeaturedProperties = async () => {
  try {
    const { data, error } = await supabase?.rpc('get_active_featured_properties');

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch featured properties' };
  }
};

// Get all featured properties (admin/agent access)
export const getAllFeaturedProperties = async () => {
  try {
    const { data, error } = await supabase?.from('featured_properties')?.select(`
        *,
        property:properties(
          id, title, address, price, property_type, bedrooms, bathrooms,
          surface_area, images, status,
          commune:communes(id, name, district)
        ),
        created_by_profile:user_profiles!created_by(id, full_name, email)
      `)?.order('display_order', { ascending: true });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch all featured properties' };
  }
};

// Get featured property by ID
export const getFeaturedPropertyById = async (id) => {
  try {
    const { data, error } = await supabase?.from('featured_properties')?.select(`
        *,
        property:properties(
          id, title, address, price, property_type, bedrooms, bathrooms,
          surface_area, images, videos, status, description, features,
          commune:communes(id, name, district, description, amenities),
          agent:user_profiles!agent_id(id, full_name, email, phone)
        ),
        created_by_profile:user_profiles!created_by(id, full_name, email)
      `)?.eq('id', id)?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch featured property' };
  }
};

// Create new featured property (admin/agent only)
export const createFeaturedProperty = async (featuredPropertyData) => {
  try {
    const { data, error } = await supabase?.from('featured_properties')?.insert([featuredPropertyData])?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create featured property' };
  }
};

// Update featured property (admin/creator only)
export const updateFeaturedProperty = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('featured_properties')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update featured property' };
  }
};

// Delete featured property (admin/creator only)
export const deleteFeaturedProperty = async (id) => {
  try {
    const { error } = await supabase?.from('featured_properties')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete featured property' };
  }
};

// ✅ CORRECTED: Increment featured property click count
export const incrementFeaturedPropertyClicks = async (id) => {
  try {
    const { data, error } = await supabase?.rpc('increment_featured_property_clicks', { featured_property_id: id });

    if (error) {
      return { error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to increment clicks' };
  }
};

// ✅ CORRECTED: This is an alias for backward compatibility - use this in your click handlers
export const incrementClickCount = incrementFeaturedPropertyClicks;

// Increment featured property inquiry count
export const incrementFeaturedPropertyInquiries = async (id) => {
  try {
    const { data, error } = await supabase?.rpc('increment_featured_property_inquiries', { featured_property_id: id });

    if (error) {
      return { error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to increment inquiries' };
  }
};

// ✅ EXPORT OBJECT: Make sure all service methods are properly exported
export const featuredPropertiesService = {
  getActiveFeaturedProperties,
  getAllFeaturedProperties,
  getFeaturedPropertyById,
  createFeaturedProperty,
  updateFeaturedProperty,
  deleteFeaturedProperty,
  incrementFeaturedPropertyClicks,
  incrementClickCount, // Alias for compatibility
  incrementFeaturedPropertyInquiries
};

export default {
  getActiveFeaturedProperties,
  getAllFeaturedProperties,
  getFeaturedPropertyById,
  createFeaturedProperty,
  updateFeaturedProperty,
  deleteFeaturedProperty,
  incrementFeaturedPropertyClicks,
  incrementClickCount, // Alias for compatibility
  incrementFeaturedPropertyInquiries
};